<script setup lang="ts">
import { mapValues, pickBy, isNil } from 'lodash-es';
import { USER_CONTACT_DESCRIPTIONS } from '../contacts';
import { type UserContact } from '../contacts';
import { RightArrowIcon } from '@glow/shared/components/icons';
import { useOperatingSystem } from '@glow/shared/composables/useOperatingSystem';
import { type FragmentType } from '@/gql';
import { contactFieldsFragment } from '@/features/users';

const props = withDefaults(
  defineProps<{
    clickBehavior?: 'link' | 'event';
    displayEmptyItems?: boolean;
    fields: FragmentType<typeof contactFieldsFragment>;
    only?: UserContact[];
  }>(),
  {
    clickBehavior: 'link',
    displayEmpty: false,
    only: undefined,
  }
);
const emit = defineEmits<{
  (e: 'click', item: UserContact): void;
}>();

const fields = useFragmentData(contactFieldsFragment, () => props.fields);
const os = useOperatingSystem();

const descriptions = computed(() => {
  const descriptionsWithValue = mapValues(
    USER_CONTACT_DESCRIPTIONS,
    (description) => ({
      ...description,
      value: fields.value[description.field],
    })
  );
  return pickBy(
    descriptionsWithValue,
    (description, item) =>
      (!props.only || props.only.includes(item as UserContact)) &&
      (props.displayEmptyItems || !isNil(description.value))
  );
});

function onClick(socialName: UserContact) {
  if (props.clickBehavior !== 'event') return;
  emit('click', socialName);
}
</script>

<template>
  <AppGroup>
    <AppButton
      v-for="[item, description] in Object.entries(descriptions)"
      :key="item"
      :class="$style.item"
      :to="
        props.clickBehavior === 'link' && description.value && description.value
          ? description.linkFormatter(description.value, os)
          : undefined
      "
      target="_blank"
      size="sm"
      variant="secondary"
      text-align="start"
      fluid
      :color="description.value ? 'dark' : 'secondary'"
      :icon-start="description.Icon"
      :icon-end="RightArrowIcon"
      @click="onClick(item as UserContact)"
    >
      {{
        description.value
          ? description.nameFormatter(description.value)
          : description.title
      }}
    </AppButton>
    <div v-if="Object.keys(descriptions).length === 0" class="p-2 text-center">
      Пусто
    </div>
  </AppGroup>
</template>

<style module>
.item {
  border-radius: 0;
}
</style>
